/**
 * Phase 6 — Sales Wizard (6 steps).
 *
 * Premium-feel guided onboarding that sits inside the public sales chat.
 * - Each step is a quick-reply card with chips.
 * - Every choice is mirrored into the chat as a `visitor` message AND tracked
 *   into `sales_messages` via `trackMessage` (so managers see it under Ventas).
 * - Final step opens the auth panel to capture email / create account.
 *
 * The wizard is non-blocking: visitors can skip and type freely at any time.
 */
import { useState, useEffect } from 'react';
import { ArrowRight, Sparkles, Check } from 'lucide-react';

const WIZARD_KEY = 'ruido_sales_wizard_v1';

const STEPS = [
  {
    id: 'business',
    prompt: 'What kind of business are you in?',
    chips: [
      { v: 'restaurant', l: 'Restaurant' },
      { v: 'fashion', l: 'Fashion' },
      { v: 'realestate', l: 'Real estate' },
      { v: 'saas', l: 'SaaS / Tech' },
      { v: 'creator', l: 'Creator' },
      { v: 'other', l: 'Other' },
    ],
  },
  {
    id: 'goal',
    prompt: 'What do you want to achieve with your video?',
    chips: [
      { v: 'awareness', l: 'Brand awareness' },
      { v: 'sales', l: 'Sell a product' },
      { v: 'launch', l: 'Launch something new' },
      { v: 'social', l: 'Grow on social' },
      { v: 'story', l: 'Tell a story' },
    ],
  },
  {
    id: 'size',
    prompt: 'What size do you have in mind?',
    chips: [
      { v: 'short',   l: 'Short · 10–15s · 1 cr.' },
      { v: 'medium',  l: 'Medium · 20–30s · 2 cr.' },
      { v: 'large',   l: 'Large · 40–45s · 4 cr.' },
      { v: 'premium', l: 'Premium · 50–60s · 6 cr.' },
    ],
  },
  {
    id: 'urgency',
    prompt: 'When do you need it?',
    chips: [
      { v: 'today',  l: 'Today / tomorrow' },
      { v: 'week',   l: 'This week' },
      { v: 'month',  l: 'This month' },
      { v: 'flex',   l: 'No rush' },
    ],
  },
  {
    id: 'volume',
    prompt: 'Is this a one-off video or recurring production?',
    chips: [
      { v: 'one',     l: '1 video' },
      { v: 'few',     l: '2–5 videos' },
      { v: 'many',    l: '5+ videos' },
      { v: 'unsure',  l: "I'm not sure" },
    ],
  },
];

function loadStored() {
  try {
    const raw = localStorage.getItem(WIZARD_KEY);
    return raw ? JSON.parse(raw) : { stepIdx: 0, answers: {} };
  } catch { return { stepIdx: 0, answers: {} }; }
}

function persist(state) {
  try { localStorage.setItem(WIZARD_KEY, JSON.stringify(state)); } catch { /* noop */ }
}

export default function SalesWizard({
  onAnswer,        // (step, value, label) => void  (mirror chip into chat + track)
  onComplete,      // (answers) => void  (open auth / show CTA)
  onSkip,          // () => void
  isLoggedIn,
}) {
  const [state, setState] = useState(loadStored);
  const [done, setDone] = useState(state.stepIdx >= STEPS.length);
  // Step 1 lives in a collapsed "pill rail" by default so it doesn't
  // dominate the page. It only expands into a full card when the user
  // picks "Other" (to type a custom value) or keeps scrolling — which
  // means answering a chip IMMEDIATELY advances to step 2 without a
  // separate expansion state. Explicit expansion is reserved for the
  // custom-text path.
  const [step1Custom, setStep1Custom] = useState(false);
  const [step1OtherText, setStep1OtherText] = useState('');

  // Auto-hide the wizard once the visitor signs up.
  useEffect(() => {
    if (isLoggedIn && !done) {
      setDone(true);
      try { localStorage.removeItem(WIZARD_KEY); } catch { /* noop */ }
    }
  }, [isLoggedIn, done]);

  if (done || isLoggedIn) return null;

  const step = STEPS[state.stepIdx];
  const totalSteps = STEPS.length + 1; // +1 for the final CTA
  const progress = state.stepIdx + 1;

  const pickChip = (chip) => {
    const newAnswers = { ...state.answers, [step.id]: chip.v };
    onAnswer?.(step, chip.v, chip.l, state.stepIdx + 1);
    if (state.stepIdx + 1 >= STEPS.length) {
      // Wizard finished — open final CTA stage
      const newState = { stepIdx: STEPS.length, answers: newAnswers };
      setState(newState);
      persist(newState);
    } else {
      const newState = { stepIdx: state.stepIdx + 1, answers: newAnswers };
      setState(newState);
      persist(newState);
    }
  };

  const finish = () => {
    setDone(true);
    try { localStorage.removeItem(WIZARD_KEY); } catch { /* noop */ }
    onComplete?.(state.answers);
  };

  const skip = () => {
    setDone(true);
    try { localStorage.removeItem(WIZARD_KEY); } catch { /* noop */ }
    onSkip?.();
  };

  // Final-stage CTA card
  if (state.stepIdx >= STEPS.length) {
    return (
      <div data-testid="sales-wizard-cta" className="px-4">
        <div className="max-w-3xl mx-auto border border-gray-200/70 rounded-2xl bg-white/60 backdrop-blur-sm p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-emerald-500" />
            <span className="text-[11px] uppercase tracking-[0.15em] text-gray-400">Almost ready</span>
            <span className="ml-auto text-[11px] text-gray-400 tabular-nums">{totalSteps}/{totalSteps}</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 font-['Outfit'] leading-snug">
            Create your account and we'll send your brief to the team.
          </h3>
          <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
            We'll give you a trial credit for your first video and save the answers you gave above.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button
              data-testid="wizard-cta-create"
              onClick={finish}
              className="px-4 py-2 rounded-xl text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white transition-colors flex items-center gap-1.5"
            >
              Create account <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="wizard-cta-skip"
              onClick={skip}
              className="px-4 py-2 rounded-xl text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              Keep reading
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isStep1 = state.stepIdx === 0;

  const submitStep1Other = () => {
    const label = step1OtherText.trim();
    if (!label) return;
    pickChip({ v: 'other', l: label });
    setStep1Custom(false);
    setStep1OtherText('');
  };

  // ── Step 1 — collapsed horizontal rail (compact, non-intrusive).
  // Turns into a full card only when the user chooses "Other" to type
  // a custom business type. Picking any other chip advances straight
  // to step 2, which uses the standard card layout.
  if (isStep1 && !step1Custom) {
    return (
      <div data-testid="sales-wizard" className="px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between gap-3 mb-1 px-1">
            <span className="text-[11px] uppercase tracking-[0.15em] text-gray-400">
              Step 1 of {totalSteps}
            </span>
            <button onClick={skip} data-testid="wizard-skip" className="text-[11px] text-gray-400 hover:text-gray-700 transition-colors">
              Skip
            </button>
          </div>
          <div
            data-testid="wizard-step1-compact"
            className="flex items-center gap-2 border border-gray-200/70 rounded-full bg-white/60 backdrop-blur-sm px-3 py-1.5 transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
          >
            <span className="text-[13px] text-gray-500 shrink-0 hidden sm:block">
              {step.prompt}
            </span>
            <span className="text-[13px] text-gray-500 shrink-0 sm:hidden">
              Your business
            </span>
            <div
              data-testid="wizard-step1-chips"
              className="flex-1 min-w-0 flex items-center gap-1.5 overflow-x-auto scrollbar-none"
              style={{ scrollbarWidth: 'none' }}
            >
              {step.chips.map((c) => (
                <button
                  key={c.v}
                  data-testid={`wizard-chip-${step.id}-${c.v}`}
                  onClick={() => {
                    if (c.v === 'other') {
                      setStep1Custom(true);
                      return;
                    }
                    pickChip(c);
                  }}
                  className="shrink-0 px-3 py-1 rounded-full text-[12px] bg-white/70 border border-gray-200/70 text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-all active:scale-[0.97]"
                >
                  {c.l}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-1 h-0.5 w-full bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gray-900/80 transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
              style={{ width: `${(progress / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sales-wizard" className="px-4">
      <div
        data-testid={isStep1 ? 'wizard-step1-expanded' : 'wizard-step-card'}
        className="max-w-3xl mx-auto border border-gray-200/70 rounded-2xl bg-white/60 backdrop-blur-sm p-4 sm:p-5 transition-all duration-[350ms] ease-[cubic-bezier(0.22,1,0.36,1)] animate-fade-in"
      >
        <div className="flex items-center gap-2 mb-2">
          <Check className="w-3.5 h-3.5 text-gray-300" />
          <span className="text-[11px] uppercase tracking-[0.15em] text-gray-400">Step {progress} of {totalSteps}</span>
          <span className="ml-auto text-[11px] text-gray-400 tabular-nums">
            <button onClick={skip} data-testid="wizard-skip" className="hover:text-gray-700 transition-colors">
              Skip
            </button>
          </span>
        </div>
        <h3 className="text-base sm:text-lg text-gray-900 font-['Outfit'] leading-snug">
          {isStep1 && step1Custom ? 'Tell us what kind of business.' : step.prompt}
        </h3>
        {isStep1 && step1Custom ? (
          <div className="mt-3 flex items-center gap-2">
            <input
              data-testid="wizard-step1-other-input"
              autoFocus
              value={step1OtherText}
              onChange={(e) => setStep1OtherText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitStep1Other(); }}
              placeholder="e.g., Marketing agency, photo studio…"
              className="flex-1 min-w-0 px-3 py-2 rounded-xl text-sm bg-white/70 border border-gray-200/70 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
            />
            <button
              data-testid="wizard-step1-other-confirm"
              onClick={submitStep1Other}
              disabled={!step1OtherText.trim()}
              className="px-4 py-2 rounded-xl text-sm font-medium bg-gray-900 hover:bg-gray-800 disabled:opacity-40 text-white transition-colors"
            >
              Continue
            </button>
            <button
              onClick={() => { setStep1Custom(false); setStep1OtherText(''); }}
              className="px-3 py-2 rounded-xl text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 mt-3">
            {step.chips.map((c) => (
              <button
                key={c.v}
                data-testid={`wizard-chip-${step.id}-${c.v}`}
                onClick={() => pickChip(c)}
                className="px-3.5 py-1.5 rounded-full text-sm bg-white/70 border border-gray-200/70 text-gray-700 hover:text-gray-900 hover:border-gray-300 transition-all active:scale-[0.97]"
              >
                {c.l}
              </button>
            ))}
          </div>
        )}
        {/* progress bar */}
        <div className="mt-4 h-0.5 w-full bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gray-900/80 transition-all"
            style={{ width: `${(progress / totalSteps) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
